import { useNavigate } from 'react-router-dom';
import { User, Envelope, Phone, MapPin, Buildings } from '@phosphor-icons/react';
import WhatsAppLink from '@/components/WhatsAppLink';
import { PersonLink } from '@/components/PersonLink';

// Renders a text blob preserving line-breaks (no HTML execution — XSS-safe).
function HtmlContent({ html, className = '' }) {
  if (!html) return <span>-</span>;
  const lines = String(html).split(/\r?\n/);
  return (
    <span className={className}>
      {lines.map((line, i) => (
        <span key={`ln-${i}-${line.slice(0, 8)}`}>{line}{i < lines.length - 1 && <br />}</span>
      ))}
    </span>
  );
}

const fmtEUR = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

/**
 * Top row of the Deal Detail page — two cards side-by-side:
 * - "Detalhes": deal fields (referência, datas, valores)
 * - "Contacto": lead/client contact info + registered client contacts list
 */
export default function DealInfoPanels({ detalhes, contactosCliente }) {
  const navigate = useNavigate();
  const d = detalhes;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      {/* Detalhes */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 lg:col-span-2">
        <h3 className="text-sm font-semibold text-[#111827] mb-4 pb-3 border-b border-[#E5E7EB]">Detalhes</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-[#6B7280]">Referência:</span> <span className="font-medium">{d.Referencia || '-'}</span></div>
          <div><span className="text-[#6B7280]">Descrição:</span> <span className="font-medium">{d.Descricao || '-'}</span></div>
          <div><span className="text-[#6B7280]">Valor Previsto:</span> <span className="font-mono font-bold text-[#0F1829]">{fmtEUR(d.ValorPrevisto)}</span></div>
          <div><span className="text-[#6B7280]">Valor Real:</span> <span className="font-mono">{fmtEUR(d.ValorReal)}</span></div>
          <div><span className="text-[#6B7280]">Data Registo:</span> <span>{d.DataRegisto || '-'}</span></div>
          <div><span className="text-[#6B7280]">Data Início:</span> <span>{d.DataInicio || '-'}</span></div>
          <div><span className="text-[#6B7280]">Prev. Fecho:</span> <span>{d.DataPrevistaFecho || '-'}</span></div>
          <div><span className="text-[#6B7280]">Data Fecho:</span> <span>{d.DataFecho || '-'}</span></div>
          {d.ID_Encomenda && (
            <div>
              <span className="text-[#6B7280]">Encomenda:</span>{' '}
              <span onClick={() => navigate(`/encomendas/${d.ID_Encomenda}`)} className="font-mono text-[#0F1829] underline cursor-pointer">#{d.ID_Encomenda}</span>
            </div>
          )}
          <div className="col-span-2"><span className="text-[#6B7280]">Observação:</span> <HtmlContent html={d.Observacao} /></div>
        </div>
      </div>

      {/* Contacto */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
        <h3 className="text-sm font-semibold text-[#111827] mb-4 pb-3 border-b border-[#E5E7EB]">Contacto</h3>
        <div className="space-y-2 text-sm">
          {d.NomeContacto && <div className="flex items-center gap-2"><User size={14} className="text-[#6B7280]" /><span>{d.NomeContacto}</span></div>}
          {d.EmailContacto && (
            <div className="flex items-center gap-2" data-testid="deal-contact-email-lead">
              <Envelope size={14} className="text-[#6B7280]" />
              <a href={`mailto:${d.EmailContacto}`} className="text-[#0F1829] hover:underline">{d.EmailContacto}</a>
            </div>
          )}
          {d.TelemovelContacto && (
            <div className="flex items-center gap-2" data-testid="deal-contact-phone-lead">
              <Phone size={14} className="text-[#6B7280]" />
              <a href={`tel:${d.TelemovelContacto}`} className="text-[#0F1829] hover:underline">{d.TelemovelContacto}</a>
              <WhatsAppLink
                phone={d.TelemovelContacto}
                idNegocio={d.ID_Negocio}
                firstName={(d.NomeContacto || '').split(' ')[0] || ''}
              />
            </div>
          )}
          {d.LocalidadeContacto && <div className="flex items-center gap-2"><MapPin size={14} className="text-[#6B7280]" /><span>{d.LocalidadeContacto}</span></div>}
          {d.Cliente_NomeCompleto && (
            <div className="flex items-center gap-2" data-testid="deal-cliente-perfil">
              <User size={14} className="text-[#6B7280]" />
              <PersonLink id={d.IdContacto} name={d.Cliente_NomeCompleto} className="font-medium" />
            </div>
          )}

          {(contactosCliente || []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#E5E7EB] space-y-1.5" data-testid="deal-client-contacts">
              <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1.5">
                Contactos registados do cliente
              </p>
              {contactosCliente.map((c) => {
                const isEmail = /email/i.test(c.TipoContacto || '');
                const Icon = isEmail ? Envelope : Phone;
                const href = isEmail ? `mailto:${c.Contacto}` : `tel:${c.Contacto}`;
                return (
                  <div
                    key={c.ID_ClienteContacto}
                    data-testid={`deal-client-contact-${c.ID_ClienteContacto}`}
                    className="flex items-center gap-2"
                  >
                    <Icon size={14} className="text-[#6B7280]" />
                    <a href={href} className="text-[#0F1829] hover:underline">{c.Contacto}</a>
                    <span className="text-[9px] uppercase tracking-[0.1em] text-[#6B7280]">{c.TipoContacto}</span>
                    {c.Principal === 'S' && (
                      <span className="text-[9px] uppercase tracking-[0.1em] font-semibold px-1.5 py-0.5 rounded bg-[#0F1829] text-white">
                        Principal
                      </span>
                    )}
                    {!isEmail && (
                      <WhatsAppLink
                        phone={c.Contacto}
                        idNegocio={d.ID_Negocio}
                        firstName={(d.NomeContacto || d.Cliente_NomeCompleto || '').split(' ')[0] || ''}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {(d.NomeEmpresa || d.NIF) && (
            <div className="mt-3 pt-3 border-t border-[#E5E7EB] space-y-1">
              {d.NomeEmpresa && (
                <div className="flex items-center gap-2">
                  <Buildings size={14} className="text-[#6B7280]" />
                  <span className="font-medium" data-testid="deal-empresa-nome">{d.NomeEmpresa}</span>
                </div>
              )}
              {d.NIF && (
                <div className="flex items-center gap-2 pl-6 text-[12px] text-[#4B5563]" data-testid="deal-empresa-nif">
                  NIF <span className="font-mono">{d.NIF}</span>
                  {d.LocalidadeEmpresa && d.LocalidadeEmpresa !== 'Não Especificado' && (
                    <span className="text-[#978D78]">· {d.LocalidadeEmpresa}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
